import type { JSX, ReactNode } from "react";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { createTranslator, detectLocale, LOCALES, type Locale, type Translator } from "./index.js";

/**
 * i18n'in React'a bağlanması.
 *
 * ⚠️ **Bir i18n kütüphanesi yoktur** (Rapor 14 §3.2 — kritik yol ≤70 KB).
 *    Bağlam + `useMemo` toplam ~40 satırdır; `react-i18next` 12 KB'dır.
 *
 * ⚠️ `<html lang>` **çalışma zamanında** güncellenir. Yanlış `lang`:
 *    - ekran okuyucu Türkçe metni İngilizce telaffuz eder,
 *    - tarayıcı yanlış tireleme/heceleme uygular.
 *    Bu, kozmetik değil **erişilebilirlik** meselesidir (Rapor 09 §11.8).
 */

const I18nContext = createContext<Translator | null>(null);
const LocaleSetter = createContext<((l: Locale) => void) | null>(null);

/**
 * ⭐ **Dil tercihi kalıcıdır** — `localStorage`.
 *
 * ⚠️ `sessionStorage` YETMEZ: kullanıcı dili bir kez seçer, her sekmede
 *    yeniden seçmek istemez. Saklanan şey bir dil kodudur; kişisel veri
 *    veya kimlik bilgisi değildir.
 */
const LOCALE_KEY = "centvox:locale";

function storedLocale(): Locale | null {
  try {
    const v = localStorage.getItem(LOCALE_KEY);
    return (LOCALES as readonly string[]).includes(v ?? "") ? (v as Locale) : null;
  } catch {
    return null;
  }
}

export function I18nProvider(props: { locale?: Locale; children: ReactNode }): JSX.Element {
  /**
   * ⚠️ Öncelik: **açık prop → kullanıcı tercihi → tarayıcı dili.**
   *    Tarayıcı dili öne alınsaydı, kullanıcının bilinçli seçimi her
   *    açılışta ezilirdi.
   */
  const [chosen, setChosen] = useState<Locale | null>(() => storedLocale());
  const locale = props.locale ?? chosen ?? detectLocale();
  const translator = useMemo(() => createTranslator(locale), [locale]);

  const setLocale = useCallback((next: Locale) => {
    setChosen(next);
    try {
      localStorage.setItem(LOCALE_KEY, next);
    } catch {
      /* kota/gizli mod — seçim bu oturumda yine geçerli */
    }
  }, []);

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  return (
    <I18nContext.Provider value={translator}>
      <LocaleSetter.Provider value={setLocale}>{props.children}</LocaleSetter.Provider>
    </I18nContext.Provider>
  );
}

/**
 * ⚠️ Sağlayıcı yoksa **boş işlev** döner, fırlatmaz: dil değiştirici isteğe
 *    bağlı bir süstür ve yokluğu sayfayı çökertmemelidir. `useI18n` ise
 *    fırlatır — çeviri olmadan sayfa zaten çizilemez.
 */
export function useSetLocale(): (l: Locale) => void {
  return useContext(LocaleSetter) ?? (() => undefined);
}

/**
 * ⚠️ Sağlayıcı yoksa **düşer**. Sessizce yedek çevirmen üretmek, eksik
 *    sağlayıcıyı üretime kadar gizler; hata testte patlamalıdır.
 */
export function useI18n(): Translator {
  const t = useContext(I18nContext);
  if (t === null) throw new Error("i18n_provider_missing");
  return t;
}
